import { Link, Tooltip } from '@mui/material'
import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import { isNil, uniqueId } from 'lodash'
import React from 'react'

import { Icon } from '#/components/Icon'
import { OutdatedData } from '#/components/OutdatedData'
import { outdatedRules, verifyIfOutdated } from '#/components/OutdatedData/rules'
import ValueVariationWrapper from '#/components/ValueVariationWrapper'
import Colors from '#/styles/Colors'
import Fonts from '#/styles/Fonts'
import { HSLDarker } from '#/utils/colorManipulation'
import { getLink, getLinkTooltip } from '#/utils/getLink'

const styles = {
  insights: {
    warning: {
      'backgroundColor': Colors.feedback.warning.backgroundColor,
      'color': Colors.feedback.warning.color,
      '&:hover': {
        backgroundColor: HSLDarker(Colors.feedback.warning.backgroundColor, 15),
      },
    },
    default: {
      color: 'black',
      minWidth: 'unset',
      width: 'fit-content',
      paddingRight: 1.5,
      paddingLeft: 1.5,
      fontSize: 14,
      minHeight: 'unset',
      height: '25px',
    },
  },
}

/**
 * Renders a button with an optional icon and tooltip that links to a
 * specified page using a dynamically generated URL
 * @param insight - Object with the type and message used when the data has a alteration indicator.
 * @param linkTo - String of the module name for the generetad URL.
 * @param slug - Unique identifier string of the page or resource.
 * @param tab - Specific tab string of the page that the link should navigate to.
 * @param filters - Object that contains filters to be applied to the target URL.
 * @param text - The text to display on the button.
 * @param tooltip - String that is used as the text for the tooltip
 * @param icon - String or a React component of the icon displayed on the button.
 * @param timestamp - Timestamp that represents when the data was last updated.
 * @returns A button with a link to a website.
 */
const TypeButtonLink = props => {
  const { insight, slug, linkTo, filters, text, sx, icon, timestamp, variation } = props

  const link = getLink({
    id: linkTo,
    params: { batch: slug },
    query: filters ? { filters } : undefined,
  })

  let buttonIcon = null
  let insightStyle = null
  let tooltipText = getLinkTooltip(linkTo) ?? ''

  const outdatedRule = verifyIfOutdated({ rules: outdatedRules.batch, timestamp })

  const allowInsight = !outdatedRule?.hideValue

  if (allowInsight) {
    switch (insight?.type) {
      case 'warning':
        insightStyle = { ...styles.insights.warning }
        buttonIcon = <Icon size="large" name="priority-high" sx={{ fontSize: 14 }} />
        tooltipText = insight.message
        break

      case 'regular':
      default:
        if (icon) {
          buttonIcon = icon
        }
    }
  }

  const content = (
    <ValueVariationWrapper variation={variation} isOutdated={!!outdatedRule}>
      <Button sx={{ ...styles.insights.default, ...insightStyle, ...sx }}>
        <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
          {buttonIcon}
          {!isNil(text) && (
            <OutdatedData
              timestamp={timestamp}
              rules={outdatedRules.batch}
              style={{ fontWeight: Fonts.fontWeight.medium }}
              allowTooltip={!tooltipText}
            >
              {text}
            </OutdatedData>
          )}
          {props.children}
        </Stack>
      </Button>
    </ValueVariationWrapper>
  )

  if (!link) return content

  return (
    <Tooltip title={tooltipText}>
      <Link href={link} underline="none" target="_blank" key={uniqueId()}>
        {content}
      </Link>
    </Tooltip>
  )
}
export default TypeButtonLink
